import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getToken, getUser } from 'ducks/auth/selectors';
import { fetchLuzmoEmbedToken } from 'services/luzmo';

// refresh a little before the token actually expires
const REFRESH_MARGIN_MS = 60 * 1000;
const MIN_REFRESH_DELAY_MS = 60 * 1000;

const useLuzmoAuth = () => {
  const user = useSelector(getUser);
  const { search } = useLocation();
  const selectedTeam = new URLSearchParams(search).get('team') || undefined;
  const sessionToken = useSelector(getToken);
  const [access, setAccess] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const timeoutRef = useRef(null);

  const uid = user?.uid || user?.ac_id;
  const org = user?.org;

  useEffect(() => {
    let cancelled = false;
    setError(null);

    const load = async () => {
      if (!sessionToken) {
        setError('Please sign in to access AI Insights.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const token = await fetchLuzmoEmbedToken(selectedTeam);

        if (cancelled) return;

        // Publish the credentials and their scope together. React 16 does not
        // batch these promise-driven updates automatically.
        setAccess({ ...token, sessionToken, uid, org, selectedTeam });

        const expiryDelay = new Date(token.expiry).getTime() - Date.now();
        const refreshDelay = Math.max(
          expiryDelay - REFRESH_MARGIN_MS,
          MIN_REFRESH_DELAY_MS
        );
        timeoutRef.current = setTimeout(load, refreshDelay);
      } catch (err) {
        if (!cancelled) {
          setAccess(null);
          setError(err?.message || 'Unable to load the Luzmo embed token.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [sessionToken, uid, org, selectedTeam]);

  const isCurrent = Boolean(access && access.sessionToken === sessionToken && access.uid === uid && access.org === org && access.selectedTeam === selectedTeam);
  return {
    authKey: isCurrent ? access.authKey : null,
    authToken: isCurrent ? access.authToken : null,
    isLoading, error,
    dashboardId: isCurrent ? access.dashboardId : null,
    allowedDashboardIds: isCurrent ? access.allowedDashboardIds : null,
    availableTeams: access?.sessionToken === sessionToken && access?.org === org ? access.availableTeams : [],
    selectedTeam
  };
};

export default useLuzmoAuth;
